import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery, useMutation } from '@tanstack/react-query'
import toast from 'react-hot-toast'
import Spinner from '../../components/Spinner'
import TermPicker from '../../components/TermPicker'
import { calcPreviewByPrice, createCustomOrder } from '../../api/orders'
import { getSettings } from '../../api/settings'
import { useAuth } from '../../context/AuthContext'
import { useTelegram } from '../../hooks/useTelegram'
import { CreditSettings } from '../../types'

const G = '#C89B4C'
const GB = 'rgba(200,155,76,0.14)'
const BD = 'rgba(244,238,225,0.14)'
const S = 'linear-gradient(155deg, rgba(255,248,235,0.05) 0%, rgba(255,248,235,0.015) 100%)'
const GLASS = { background: S, backdropFilter: 'blur(11px) saturate(150%)', WebkitBackdropFilter: 'blur(11px) saturate(150%)' } as const
const TX = '#F4F1EA'
const TX2 = '#C8C4BB'
const TX3 = '#8A8480'
const SERIF = "'Newsreader', Georgia, serif"

function getRate(s: CreditSettings, months: number): number {
  if (months <= 3) return s.rate_3_months
  if (months <= 6) return s.rate_6_months
  if (months <= 9) return s.rate_9_months
  return s.rate_12_months
}

const fmt  = (n: number) => `$${new Intl.NumberFormat('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 }).format(n)}`
const fmtS = (n: number, rate: number) => `≈ ${Math.round(n * rate).toLocaleString('ru-RU')} сум`

export default function CustomOrderPage() {
  const navigate = useNavigate()
  const { user } = useAuth()
  const { tg } = useTelegram()
  const lang = user?.language ?? 'ru'

  const [productName, setProductName] = useState('')
  const [productPrice, setProductPrice] = useState('')
  const [downAmt, setDownAmt] = useState(0)
  const [downInput, setDownInput] = useState('0')
  const [months, setMonths] = useState(12)
  const [comment, setComment] = useState('')

  const { data: settings } = useQuery({ queryKey: ['settings'], queryFn: getSettings })

  const maxMonths    = settings?.max_months ?? 24
  const usdRate      = settings?.usd_rate ?? 12000
  const interestRate = settings ? getRate(settings, months) : null
  const price        = parseFloat(productPrice.replace(/\s/g, '')) || 0

  useEffect(() => {
    if (price > 0 && downAmt > price) { setDownAmt(0); setDownInput('0') }
  }, [price]) // eslint-disable-line

  const canCalc = price > 0 && months >= 1 && productName.trim().length >= 2

  const { data: preview, isFetching: calcLoading } = useQuery({
    queryKey: ['calc-custom', price, downAmt, months, interestRate],
    queryFn: () => calcPreviewByPrice({ product_price: price, down_payment: downAmt, months, discount_pct: user?.personal_discount ?? 0 }),
    enabled: canCalc,
    placeholderData: (prev) => prev,
  })

  const [overLimitOrder, setOverLimitOrder] = useState<{ orderId: number; overBy: number } | null>(null)

  const createMut = useMutation({
    mutationFn: () => createCustomOrder({
      product_name: productName.trim(),
      product_price: price,
      down_payment: downAmt,
      months,
      comment: comment.trim() || undefined,
    }),
    onSuccess: (order) => {
      tg?.HapticFeedback.notificationOccurred('success')
      if (order.over_limit_amount && order.over_limit_amount > 0) {
        setOverLimitOrder({ orderId: order.id, overBy: order.over_limit_amount })
      } else {
        toast.success(lang === 'uz' ? 'Ariza yaratildi!' : 'Заявка создана!')
        navigate(`/orders/${order.id}`)
      }
    },
    onError: (e: any) => toast.error(e?.response?.data?.detail ?? (lang === 'uz' ? 'Xatolik' : 'Ошибка')),
  })

  useEffect(() => {
    tg?.BackButton.show()
    const cb = () => navigate(-1)
    tg?.BackButton.onClick(cb)
    return () => { tg?.BackButton.offClick(cb); tg?.BackButton.hide() }
  }, []) // eslint-disable-line


  if (overLimitOrder) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '32px 24px' }}>
        <div style={{
          width: '100%', maxWidth: 360, borderRadius: 20,
          background: '#1A1500', border: '1.5px solid rgba(200,155,76,0.4)',
          padding: '32px 24px', textAlign: 'center',
        }}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>⚠️</div>
          <h2 style={{ fontFamily: SERIF, fontStyle: 'italic', fontSize: 22, color: G, margin: '0 0 12px' }}>
            {lang === 'uz' ? 'Limit oshib ketdi' : 'Превышен лимит'}
          </h2>
          <p style={{ fontSize: 13, color: TX2, lineHeight: 1.6, margin: '0 0 8px' }}>
            {lang === 'uz'
              ? `Arizangiz qabul qilindi, lekin kredit limitingizdan $${overLimitOrder.overBy.toFixed(2)} oshib ketdi.`
              : `Заявка принята, но вы превысили кредитный лимит на $${overLimitOrder.overBy.toFixed(2)}.`}
          </p>
          <p style={{ fontSize: 12, color: TX3, lineHeight: 1.55, margin: '0 0 28px' }}>
            {lang === 'uz'
              ? "Arizangiz ko'rib chiqiladi, lekin ko'proq vaqt ketishi mumkin."
              : 'Заявка будет рассмотрена, но может занять больше времени.'}
          </p>
          <button
            onClick={() => navigate(`/orders/${overLimitOrder.orderId}`)}
            style={{
              width: '100%', padding: '13px 0', borderRadius: 12, fontSize: 14, fontWeight: 600,
              background: GB, border: `1.5px solid ${G}`, color: G, cursor: 'pointer',
            }}
          >
            {lang === 'uz' ? 'Arizani ko\'rish' : 'Перейти к заявке'}
          </button>
        </div>
      </div>
    )
  }

  return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <header style={{
        position: 'sticky', top: 0, zIndex: 40, padding: '10px 20px',
        background: 'rgba(13,21,38,0.82)', backdropFilter: 'blur(16px)',
        WebkitBackdropFilter: 'blur(16px)', borderBottom: `1px solid ${BD}`,
        display: 'flex', alignItems: 'center', gap: 12,
      }}>
        <button onClick={() => navigate(-1)} style={{ background: 'none', border: 'none', color: TX3, cursor: 'pointer', padding: 0, display: 'flex', alignItems: 'center' }}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M15 18l-6-6 6-6"/></svg>
        </button>
        <span style={{ fontSize: 11, fontWeight: 600, color: TX3, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
          {lang === 'uz' ? 'Yangi ariza' : 'Новая заявка'}
        </span>
      </header>

      <main style={{ flex: 1, padding: '20px 20px 40px', overflowY: 'auto' }}>
        <h1 style={{ fontFamily: SERIF, fontStyle: 'italic', fontSize: 28, color: TX, margin: '0 0 6px', lineHeight: 1.15 }}>
          {lang === 'uz' ? 'Ariza qoldirish' : 'Отправить заявку'}
        </h1>
        <p style={{ margin: '0 0 24px', fontSize: 13, color: TX3 }}>
          {lang === 'uz' ? "Istalgan mahsulotga nasiya ariza bering" : 'Любой товар — мы рассчитаем рассрочку'}
        </p>

        {/* Product name */}
        <div style={{ marginBottom: 18 }}>
          <Label text={lang === 'uz' ? 'MAHSULOT NOMI' : 'НАЗВАНИЕ ТОВАРА'} />
          <input
            className="input"
            placeholder={lang === 'ru' ? 'Например: Samsung Galaxy S24' : 'Masalan: Samsung Galaxy S24'}
            value={productName}
            onChange={e => setProductName(e.target.value)}
          />
        </div>

        {/* Price */}
        <div style={{ marginBottom: 24 }}>
          <Label text={lang === 'uz' ? "NARX ($)" : 'ЦЕНА ($)'} />
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px', borderRadius: 12, background: S, border: `1px solid ${BD}` }}>
            <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 16, color: G }}>$</span>
            <input
              type="number" inputMode="decimal"
              placeholder="800"
              value={productPrice}
              onChange={e => setProductPrice(e.target.value)}
              style={{ background: 'none', border: 'none', outline: 'none', flex: 1, fontFamily: "'JetBrains Mono', monospace", fontSize: 20, color: TX }}
            />
          </div>
          {price > 0 && (
            <p style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 10, color: TX3, marginTop: 6 }}>
              {fmtS(price, usdRate)}
            </p>
          )}
        </div>

        {/* Term */}
        <div style={{ marginBottom: 24 }}>
          <Label text={lang === 'uz' ? "BO'LIB TO'LASH MUDDATI" : 'СРОК РАССРОЧКИ'} />
          <TermPicker value={months} onChange={setMonths} max={maxMonths} lang={lang} />
        </div>

        {/* Down payment (only when price is entered) */}
        {price > 0 && (
          <div style={{ marginBottom: 24 }}>
            <Label text={lang === 'uz' ? "BOSHLANG'ICH TO'LOV" : 'ПЕРВОНАЧАЛЬНЫЙ ВЗНОС'} />
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px', borderRadius: 12, background: S, border: `1px solid ${BD}`, marginBottom: 10 }}>
              <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 16, color: G }}>$</span>
              <input
                type="number" inputMode="decimal" min={0} max={price} step={1}
                value={downInput}
                onChange={e => {
                  const v = e.target.value
                  setDownInput(v)
                  setDownAmt(Math.min(parseFloat(v) || 0, price))
                }}
                style={{ background: 'none', border: 'none', outline: 'none', flex: 1, fontFamily: "'JetBrains Mono', monospace", fontSize: 20, color: TX }}
              />
            </div>
            {downAmt > 0 && (
              <p style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 10, color: TX3, marginBottom: 10 }}>
                {fmtS(downAmt, usdRate)}
              </p>
            )}
            <input
              type="range" min={0} max={price} step={10} value={downAmt}
              onChange={e => { const v = Number(e.target.value); setDownAmt(v); setDownInput(String(v)) }}
              style={{ width: '100%' }}
            />
            <div style={{ marginTop: 10, display: 'flex', gap: 6 }}>
              {[0, 50, 100, 200].filter(a => a <= price).map(a => (
                <button
                  key={a}
                  onClick={() => { setDownAmt(a); setDownInput(String(a)) }}
                  style={{
                    padding: '5px 12px', borderRadius: 99,
                    fontFamily: "'JetBrains Mono', monospace", fontSize: 11,
                    background: downAmt === a ? GB : S,
                    color: downAmt === a ? G : TX3,
                    border: downAmt === a ? `1px solid ${G}` : `1px solid ${BD}`,
                    cursor: 'pointer',
                  }}
                >
                  ${a}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Calculation result */}
        {calcLoading && price > 0 && <Spinner className="my-4" />}
        {preview && !calcLoading && (
          <div style={{ padding: 20, borderRadius: 16, ...GLASS, border: `1px solid ${BD}`, marginBottom: 20 }}>
            <p style={{ margin: '0 0 8px', fontSize: 10, fontWeight: 600, color: G, letterSpacing: '0.10em', textTransform: 'uppercase' }}>
              {lang === 'uz' ? 'OYLIK TO\'LOV' : 'ЕЖЕМЕСЯЧНЫЙ ПЛАТЁЖ'}
            </p>
            <div style={{ marginBottom: 6 }}>
              <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 36, color: TX }}>{fmt(preview.monthly_payment)}</span>
            </div>
            <p style={{ margin: '0 0 16px', fontFamily: "'JetBrains Mono', monospace", fontSize: 10, color: TX3 }}>
              {fmtS(preview.monthly_payment, usdRate)}
            </p>
            <p style={{ margin: '0 0 16px', fontSize: 12, color: TX2 }}>
              {months} {lang === 'uz' ? "to'lov" : 'платежей'} · {lang === 'uz' ? 'Stavka' : 'Ставка'} {preview.interest_rate}%
            </p>
            <div style={{ borderTop: `1px solid ${BD}`, paddingTop: 14, display: 'flex', flexDirection: 'column', gap: 8 }}>
              <CalcRow label={lang === 'uz' ? "Boshlang'ich to'lov" : 'Первый взнос'}      value={fmt(downAmt)}                 hint={fmtS(downAmt, usdRate)} />
              <CalcRow label={lang === 'uz' ? 'Raqamlashtirish' : 'Сумма рассрочки'}       value={fmt(preview.loan_amount)}     hint={fmtS(preview.loan_amount, usdRate)} />
              <CalcRow label={lang === 'uz' ? 'Jami' : 'Итого'}                            value={fmt(preview.total_amount)}    hint={fmtS(preview.total_amount, usdRate)} />
            </div>
          </div>
        )}

        {/* Comment (show after preview) */}
        {preview && !calcLoading && (
          <div style={{ marginBottom: 24 }}>
            <Label text={lang === 'uz' ? 'IZOH (ixtiyoriy)' : 'КОММЕНТАРИЙ (необязательно)'} />
            <textarea
              className="input"
              placeholder={lang === 'ru' ? 'Пожелания, цвет, модель...' : 'Xohishlar, rang, model...'}
              value={comment}
              onChange={e => setComment(e.target.value)}
              rows={3}
              style={{ resize: 'none', lineHeight: 1.55 }}
            />
          </div>
        )}

        <button
          onClick={() => createMut.mutate()}
          disabled={!canCalc || !preview || createMut.isPending}
          style={{
            width: '100%', padding: '14px 0', borderRadius: 14, fontSize: 14, fontWeight: 600,
            background: canCalc && preview ? GB : S,
            border: `1.5px solid ${canCalc && preview ? G : BD}`,
            color: canCalc && preview ? G : TX3,
            cursor: canCalc && preview ? 'pointer' : 'not-allowed',
            opacity: createMut.isPending ? 0.5 : 1,
            marginBottom: 32,
          }}
        >
          {createMut.isPending
            ? (lang === 'uz' ? 'Yuklanmoqda...' : 'Загрузка...')
            : (lang === 'uz' ? 'Arizani yuborish' : 'Отправить заявку')}
        </button>
      </main>
    </div>
  )
}

function Label({ text }: { text: string }) {
  return <p style={{ margin: '0 0 10px', fontSize: 10, fontWeight: 600, color: '#8A8480', letterSpacing: '0.10em', textTransform: 'uppercase' }}>{text}</p>
}

function CalcRow({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
      <span style={{ fontSize: 12, color: '#8A8480' }}>{label}</span>
      <div style={{ textAlign: 'right' }}>
        <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 13, color: '#F4F1EA' }}>{value}</span>
        {hint && <p style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9, color: '#8A8480', margin: '1px 0 0' }}>{hint}</p>}
      </div>
    </div>
  )
}
